import { useState } from "react";
import styled from "styled-components";
import { FiChevronRight } from "react-icons/fi";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { Paragraph } from "../Paragraph";

export interface SideNavItem {
  key: string;
  label: string;
  count?: number;
  content: React.ReactNode;
}

export interface SideNavGroup {
  key: string;
  label: string;
  description?: string;
  items: SideNavItem[];
}

const Layout = styled(Flex)`
  ${({ theme: { breakpoints, space } }) => `
    align-items: flex-start;
    gap: ${space[3]};

    @media (max-width: ${breakpoints[1]}) {
      flex-direction: column;
      gap: ${space[2]};
    }
  `}
`;

const Nav = styled(Box)`
  ${({ theme: { breakpoints, colors, space } }) => `
    width: 200px;
    flex-shrink: 0;
    border-right: 1px solid ${colors.gray[10]};
    padding-right: ${space[2]};

    @media (max-width: ${breakpoints[1]}) {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid ${colors.gray[10]};
      padding-right: 0;
      padding-bottom: ${space[1]};
    }
  `}
`;

/**
 * The content pane must be allowed to shrink below its content, or a single
 * unbreakable string (a long URL, a wide table) widens the whole container and
 * the layout jumps as the user moves between items. `min-width: 0` permits the
 * shrink; `overflow-x: auto` means genuinely wide content scrolls inside the
 * pane rather than pushing everything around it.
 */
const Content = styled(Box)`
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
`;

const GroupLabel = styled(Paragraph)`
  ${({ theme: { colors, fontSizes, fontWeights, space } }) => `
    margin: ${space[2]} 0 ${space[0]};
    font-size: ${fontSizes[0]};
    font-weight: ${fontWeights.semibold};
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${colors.gray[50]};

    &:first-child {
      margin-top: 0;
    }
  `}
`;

const GroupDescription = styled(Paragraph)`
  ${({ theme: { colors, fontSizes, space } }) => `
    margin: 0 0 ${space[1]};
    font-size: ${fontSizes[0]};
    color: ${colors.gray[40]};
  `}
`;

const NavItem = styled.button<{ isActive: boolean; isEmpty: boolean }>`
  ${({
    theme: { colors, fontFamily, fontSizes, fontWeights, radii, space },
    isActive,
    isEmpty,
  }) => `
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space[1]};
    width: 100%;
    padding: ${space[1]};
    margin-bottom: 4px;
    border: none;
    border-radius: ${radii[0]};
    cursor: pointer;
    text-align: left;
    font-family: ${fontFamily};
    font-size: ${fontSizes[1]};
    font-weight: ${isActive ? fontWeights.semibold : fontWeights.medium};
    color: ${
      isActive ? colors.blue.primary : isEmpty ? colors.gray[30] : colors.gray[60]
    };
    background: ${isActive ? colors.blue[50] : "transparent"};
    transition: color 0.15s ease, background 0.15s ease;

    &:hover {
      background: ${isActive ? colors.blue[50] : colors.gray[10]};
      color: ${
        isActive
          ? colors.blue.primary
          : isEmpty
            ? colors.gray[40]
            : colors.gray[80]
      };
    }

    &:focus-visible {
      outline: 2px solid ${colors.blue.primary};
      outline-offset: 2px;
    }
  `}
`;

const Count = styled.span<{ isActive: boolean; isEmpty: boolean }>`
  ${({ theme: { colors, fontSizes, fontWeights }, isActive, isEmpty }) => `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    font-size: ${fontSizes[0]};
    font-weight: ${fontWeights.semibold};
    background: ${
      isActive ? colors.blue.primary : isEmpty ? colors.gray[10] : colors.gray[20]
    };
    color: ${
      isActive ? colors.white : isEmpty ? colors.gray[40] : colors.gray[60]
    };
  `}
`;

const Chevron = styled(FiChevronRight)`
  flex-shrink: 0;
  color: currentColor;
  opacity: 0.7;
`;

/**
 * A vertical navigation down the left with the selected item's content on the
 * right, in the manner of the Settings area.
 *
 * Preferred over a horizontal tab strip wherever there are more items than fit
 * on one line: a vertical list scales without wrapping, keeps every option and
 * its count readable at once, and leaves the full width to the content, which
 * matters when that content is a wide table.
 *
 * Groups are always shown in full; nothing here collapses.
 */
export const SideNavTabs = ({
  groups,
  initialKey,
}: {
  groups: SideNavGroup[];
  initialKey?: string;
}) => {
  const allItems = groups.flatMap((group) => group.items);
  const [activeKey, setActiveKey] = useState(
    initialKey || allItems[0]?.key || ""
  );

  const active =
    allItems.find((item) => item.key === activeKey) || allItems[0] || null;

  return (
    <Layout>
      <Nav>
        {groups.map((group) => (
          <Box key={group.key}>
            <GroupLabel>{group.label}</GroupLabel>
            {group.description && (
              <GroupDescription>{group.description}</GroupDescription>
            )}
            {group.items.map((item) => {
              const isActive = item.key === active?.key;
              const isEmpty = !item.count;
              return (
                <NavItem
                  key={item.key}
                  type="button"
                  isActive={isActive}
                  isEmpty={isEmpty}
                  onClick={() => setActiveKey(item.key)}
                >
                  <span>{item.label}</span>
                  <Flex alignItems="center" gap="8">
                    {item.count !== undefined && (
                      <Count isActive={isActive} isEmpty={isEmpty}>
                        {item.count}
                      </Count>
                    )}
                    <Chevron size={14} />
                  </Flex>
                </NavItem>
              );
            })}
          </Box>
        ))}
      </Nav>

      <Content>{active?.content}</Content>
    </Layout>
  );
};
